import { useState } from 'react';
import { useTheme, spacing } from '@fti/design-system';

/**
 * Collapsible helper offering an LLM prompt to brainstorm topic/subject terms.
 * Shown on the Topic Terms and Subject Terms wizard steps.
 */

const BRAINSTORM_PROMPT = `I'm setting up a media simulation that predicts how news outlets would cover an announcement from my organization. The tool works by taking each "term" I provide and searching paragraphs from a publication's past articles for the most similar coverage using cosine similarity via Milvus.

I need two separate lists:
1. Topic terms (2–7) — the themes/subject matter of the story. These should describe what the story is about in the words a reporter would use (e.g. "merger," "layoffs," "data breach," "FDA approval," "antitrust"). Include closely related phrasings a journalist might use for the same theme. These topic terms should be company/industry agnostic.
2. Subject terms (2–5) — the specific companies, people, industries involved. Include the organization's common name, any well-known short names or former names, and the names of highly visible leaders (e.g. "JPMorgan," "Chase," "Jamie Dimon").

Here's my situation:
- Organization: [your company/client name]
- What's happening: [1–3 sentences describing the announcement or event]
- Key people involved: [names + titles, if any]
- Industry: [e.g. banking, pharma, tech]

Please give me the two lists of potential terms. Do not include terms that might be too broad (returns unrelated stories) or too narrow (returns nothing).`;

export function TermBrainstormHelp() {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(BRAINSTORM_PROMPT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be unavailable; user can still select the text */
    }
  };

  return (
    <div style={{ marginTop: spacing.xs }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontSize: '0.8rem',
          fontWeight: 600,
          color: theme.colors.primary[500],
        }}
      >
        {expanded ? '▾ Struggling to come up with terms?' : '▸ Struggling to come up with terms?'}
      </button>

      {expanded && (
        <div
          style={{
            marginTop: spacing.xs,
            padding: spacing.sm,
            backgroundColor: theme.colors.background.paper,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: '4px',
          }}
        >
          <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: '0.8rem', color: theme.colors.text.secondary, lineHeight: 1.6 }}>
            Use the following prompt with FTI GPT to help brainstorm potential terms. Make sure to
            verify the terms you select return useful coverage using the article preview below!
          </p>
          <div style={{ position: 'relative' }}>
            <button
              onClick={handleCopy}
              style={{
                position: 'absolute',
                top: spacing.xs,
                right: spacing.xs,
                padding: `2px ${spacing.xs}`,
                fontSize: '0.7rem',
                fontWeight: 600,
                cursor: 'pointer',
                color: theme.colors.primary[600],
                backgroundColor: theme.colors.background.default,
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: '4px',
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <pre
              style={{
                margin: 0,
                padding: spacing.sm,
                paddingTop: spacing.lg,
                backgroundColor: theme.colors.background.default,
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: '4px',
                fontSize: '0.75rem',
                color: theme.colors.text.primary,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
              }}
            >
              {BRAINSTORM_PROMPT}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
